import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';
const AUTH_URL = `${API_URL}/authRoutes`;

// Get auth token from localStorage
const getAuthHeader = () => {
  const token = localStorage.getItem('token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const userService = {
  // Get all users with pagination and search
  getAllUsers: async ({ page = 1, limit = 5, search = '' } = {}) => {
    try {
      const response = await axios.get(`${AUTH_URL}/users`, {
        headers: getAuthHeader(),
        params: { page, limit, search }
      });
      return { success: true, data: response.data };
    } catch (error) {
      throw error.response?.data || { message: 'Failed to fetch users' };
    }
  },

  // Get user by ID
  getUserById: async (userId) => {
    try {
      const response = await axios.get(`${AUTH_URL}/users/${userId}`, {
        headers: getAuthHeader()
      });
      return { success: true, data: response.data };
    } catch (error) {
      throw error.response?.data || { message: 'Failed to fetch user' };
    }
  },

  // Create new user
  createUser: async (userData) => {
    try {
      const response = await axios.post(`${AUTH_URL}/users`, userData, {
        headers: getAuthHeader()
      });
      return { success: true, data: response.data };
    } catch (error) {
      throw error.response?.data || { message: 'Failed to create user' };
    }
  },

  // Update user
  updateUser: async (userId, userData) => {
    try {
      const response = await axios.put(`${AUTH_URL}/users/${userId}`, userData, {
        headers: getAuthHeader()
      });
      return { success: true, data: response.data };
    } catch (error) {
      throw error.response?.data || { message: 'Failed to update user' };
    }
  },

  // Delete user
  deleteUser: async (userId) => {
    try {
      const response = await axios.delete(`${AUTH_URL}/users/${userId}`, {
        headers: getAuthHeader()
      });
      return { success: true, data: response.data };
    } catch (error) {
      throw error.response?.data || { message: 'Failed to delete user' };
    }
  },

  // Get all roles
  getAllRoles: async () => {
    try {
      const response = await axios.get(`${AUTH_URL}/roles`, {
        headers: getAuthHeader(),
        params: { limit: 1000 } // Get all roles for role assignment
      });
      return { success: true, data: response.data };
    } catch (error) {
      throw error.response?.data || { message: 'Failed to fetch roles' };
    }
  },

  // Assign role to user
  assignRoleToUser: async (userId, roleId) => {
    try {
      const response = await axios.post(`${AUTH_URL}/users/${userId}/roles`,
        { roleId },
        { headers: getAuthHeader() }
      );
      return { success: true, data: response.data };
    } catch (error) {
      throw error.response?.data || { message: 'Failed to assign role' };
    }
  },

  // Remove role from user
  removeRoleFromUser: async (userId, roleId) => {
    try {
      const response = await axios.delete(`${AUTH_URL}/users/${userId}/roles/${roleId}`, {
        headers: getAuthHeader()
      });
      return { success: true, data: response.data };
    } catch (error) {
      throw error.response?.data || { message: 'Failed to remove role' };
    }
  },

  // Get WhatsApp subscriptions for a user
  getWhatsappSubscriptions: async (userId) => {
    try {
      const response = await axios.get(`${AUTH_URL}/users/${userId}/whatsapp-subscriptions`, {
        headers: getAuthHeader()
      });
      return { success: true, data: response.data };
    } catch (error) {
      throw error.response?.data || { message: 'Failed to fetch subscriptions' };
    }
  },

  // Save WhatsApp subscriptions for a user
  saveWhatsappSubscriptions: async (userId, templates) => {
    try {
      const response = await axios.put(`${AUTH_URL}/users/${userId}/whatsapp-subscriptions`,
        { templates },
        { headers: getAuthHeader() }
      );
      return { success: true, data: response.data };
    } catch (error) {
      throw error.response?.data || { message: 'Failed to save subscriptions' };
    }
  }
};

export default userService;
